/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Container, Box, Button, Typography, Stack,
} from '@mui/material';
import { PartnerStatus } from '@marageti/z4-sdk/lib/travel';
import {
  Fab,
  FormCard,
  FormCardSubtitle,
  FormCardTitle,
  FormElement,
  FormInputsStack,
  FormSubmissionError,
  getSubmitButtonProps,
  Inputs,
  SelectInputOption,
} from '@marageti/z4-lib';
import EditCountryForm from '../../components/partner-country-edit';
import { ViewProps } from './types';
import CountryCard from '../../components/partner-add/country-card';
import RemoveCountryConfirmation from '../../components/partner-add/remove-country-confirmation';

const statusOptions: SelectInputOption[] = [
  {
    label: 'Onboarding',
    value: PartnerStatus.ONBOARDING,
  },
  {
    label: 'New',
    value: PartnerStatus.NEW,
  },
  {
    label: 'Active',
    value: PartnerStatus.ACTIVE,
  },
  {
    label: 'Non-Strategic',
    value: PartnerStatus.NON_STRATEGIC,
  },
  {
    label: 'Phasing Out',
    value: PartnerStatus.PHASING_OUT,
  },
  {
    label: 'Inactive',
    value: PartnerStatus.INACTIVE,
  },
];

const AddPartnerView = ({
  countries,
  countryModalOpen,
  countryToEdit,
  countryToRemove,
  errors,
  handleSubmit,
  isSubmitting,
  isValid,
  onAddCountry,
  onCancel,
  onCancelRemoveCountry,
  onClickEditCountry,
  onClickRemoveCountry,
  onCloseCountryModal,
  onConfirmRemoveCountry,
  onOpenCountryModal,
  onSubmit,
  register,
  submissionError,
  excludeCountries,
}: ViewProps) => (
  <Container maxWidth="md">
    <Box
      sx={{
        pt: { desktop: 4, mobile: 2 },
        minHeight: '100vh',
      }}
    >
      <FormElement
        name="add-partner-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box sx={{
          mb: 7,
        }}
        >
          <Typography component="h1" variant="h2" sx={{ marginBottom: 2, width: { desktop: '662px' } }}>
            {submissionError && (
            <FormSubmissionError
              message={submissionError.message}
              errorCode={submissionError.code}
              errorId={submissionError.id}
            />
            )}
            Add Partner
          </Typography>
          <FormCard>
            <FormCardTitle>
              Basic Info
            </FormCardTitle>
            <FormCardSubtitle>
              Required to create partner.
            </FormCardSubtitle>
            <FormInputsStack>
              <Inputs.Text
                helperText={errors?.name?.message as string}
                id="name"
                label="Company Name"
                required
                {...register('name', { required: 'Please enter a company name' })}
              />
              <Inputs.Select
                defaultValue={statusOptions[0].value}
                helperText={errors?.status?.message as string}
                id="status"
                label="Status"
                options={statusOptions}
                required
                {...register('status', { required: 'Please select a status' })}
              />
            </FormInputsStack>
          </FormCard>
          <Stack
            spacing={1.5}
            sx={{
              mt: { mobile: 3, desktop: 3.75 },
              mb: '100px',
            }}
          >
            {countries && (
            <Stack spacing={1.5}>
              {countries.map((country) => (
                <CountryCard
                  onClickEdit={onClickEditCountry}
                  onClickRemove={onClickRemoveCountry}
                  partnerCountry={country}
                  key={country.country}
                />
              ))}
            </Stack>
            )}
            <Button
              variant="outlined"
              onClick={onOpenCountryModal}
              sx={{
                width: { desktop: 'fit-content' },
              }}
              type="button"
            >
              Add Country
            </Button>
          </Stack>
        </Box>
        <Fab anchorId="oryx-footer">
          <Container sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexDirection: {
              mobile: 'column',
              desktop: 'row',
            },
          }}
          >
            <Button
              type="submit"
              variant="contained"
              sx={{
                width: { desktop: 'fit-content' },
              }}
              {...getSubmitButtonProps(isSubmitting, isValid)}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
          </Container>
        </Fab>
      </FormElement>
      <EditCountryForm
        open={countryModalOpen}
        partnerCountry={countryToEdit}
        onAddCountry={onAddCountry}
        onClose={onCloseCountryModal}
        excludeCodes={excludeCountries}
      />
      <RemoveCountryConfirmation
        name={countryToRemove?.displayName || ''}
        onCancel={onCancelRemoveCountry}
        onRemove={onConfirmRemoveCountry}
        open={!!countryToRemove}
      />
    </Box>
  </Container>
);

export default AddPartnerView;
